<template>
  <div class="h-100">
    <div v-if="loadingPdf" class="d-flex align-center justify-center h-100">
      <v-progress-circular size="70" width="9" indeterminate></v-progress-circular>
    </div>

    <h3 v-else-if="!pdfStore.currentFile" class="ma-6">Mēģinot atrast failu - notika kļūda</h3>

    <div v-if="pdfUrl" class="h-100">
      <WorksheetTeacherLessonPanel
        v-if="PDFInWorksheets && (userStore.isTeacherType || userStore.isElder)"
        @open-preview="openWorksheetModal"
      />
      <WorksheetStudentLessonPanel
        v-if="PDFInWorksheets && userStore.isStudent"
        @open-worksheet="openWorksheetModal"
        @force-close-worksheet="forceCloseWorksheet"
      />
      <WorksheetModal
        :worksheet-modal-open="worksheetModalOpen"
        :preview="userStore.isTeacherType || userStore.isElder"
        @close="worksheetModalOpen = false"
      />
      <layout-force-close-worksheet-notice-modal
        :show-dialog="forceClosedDialogOpen"
        @close="forceClosedDialogOpen = false"
      />

      <iframe :src="pdfUrl" class="pdf-viewer-iframe" width="100%" height="100%" style="border: none"></iframe>

      <EklaseHomeworkSubmittionModal :pdf="true" />
      <MobileHomeworkButton v-if="$vuetify.display.xs && showHomeworkButton" />
    </div>
  </div>
</template>

<script lang="ts">
import constants from "~/constants"
import { useDrawerStore } from "~/stores/drawerStore"
import { usePdfStore } from "~/stores/pdfStore"
import { useUserStore } from "~/stores/userStore"

export default {
  setup() {
    useHead({ bodyAttrs: { "data-page": "pdf-reader" } })

    definePageMeta({
      layout: "plain",
    })

    const { buildPdfReaderUrl } = usePdfReader()
    const pdfStore = usePdfStore()
    const drawerStore = useDrawerStore()
    const userStore = useUserStore()

    return {
      pdfStore,
      userStore,
      drawerStore,
      buildPdfReaderUrl,
    }
  },

  data() {
    return {
      worksheetModalOpen: false,
      pdfUrl: null as any,
      loadingPdf: false,
      pageFollowerTimer: null as null | NodeJS.Timer,
      forceClosedDialogOpen: false,
    }
  },

  computed: {
    pdfProduct() {
      return this.pdfStore.currentFile?.product
    },

    shouldLoadWorksheet() {
      return !!this.$route.query.w
    },

    showHomeworkButton() {
      const showBasedOnRoute = constants.HOMEWORK_BUTTON_PATHS.includes(this.$route.matched[0].path)
      return showBasedOnRoute
    },

    showPrintButton() {
      return ["metodiskie-lidzekli", "darba-lapas", "parbaudes-darbi"].includes(this.$route.path.split("/")[1])
    },

    showEvenPagesButton() {
      return ["book", "teaching_aids"].includes(this.pdfProduct.variant) && this.pdfProduct.book_cover
    },

    PDFInWorksheets() {
      return this.$route.path.split("/")[1] === "darba-lapas"
    },
  },

  watch: {
    $route() {
      if (this.shouldLoadWorksheet) {
        this.fetchPdfFile()
      } else {
        this.pdfStore.currentFile = null
      }
    },
  },

  mounted() {
    if (!this.$vuetify.display.xs) {
      this.drawerStore.closeDesktopDrawer()
      this.drawerStore.openContentMenu()
    } else {
      this.drawerStore.closeMobileDrawer()
    }
    this.fetchPdfFile()
  },

  unmounted() {
    this.drawerStore.closeContentMenu()
  },

  methods: {
    openWorksheetModal() {
      this.worksheetModalOpen = true
    },

    fetchPdfFile() {
      this.loadingPdf = true
      this.pdfStore.fetchFile({
        productSlug: this.$route.params.slug as string,
        worksheetFileId: this.$route.query.w as string,
        successCallback: this.setupPdfFile,
      })
    },

    async setupPdfFile() {
      /*
        PDF.JS will check for these custom parameters:
          [+] print - decide whether to display print button
          [+] evenspreadpage - if present, will display #spreadEven.btn, otherwise #spreadOdd.btn

        Custom parameters are defined in --> [ pdf_link_service.js ] --> (search for "params.has")
        when using custom params, if its checking for presence, not value - then just pass 1 as parameters value.
      */
      const params = {} as Record<string, any>

      if (this.$route.query?.page) {
        params.page = this.$route.query?.page
      }

      if (this.showPrintButton && !this.$vuetify.display.xs) {
        params.print = 1
      }

      if (this.showEvenPagesButton) {
        params.evenspreadpage = 1
      }

      this.pdfUrl = await this.buildPdfReaderUrl((this.pdfStore.currentFile as any).file_url, params)
      this.loadingPdf = false
    },

    forceCloseWorksheet() {
      if (this.worksheetModalOpen) {
        this.worksheetModalOpen = false
        this.forceClosedDialogOpen = true
      }
    },
  },
}
</script>
